import React from "react";

import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { ConfiguratorPrice } from "../../../components/ConfiguratorPrice";
import { RichText } from "../../../components/RichText";
import { ConfiguratorTypes } from "../../../constants";
import { IProduct } from "../../../models/catalogue";
import { DiscountDisplayMode } from "../../../models/discountDisplay";
import { IImageURL, ISafeHTML, WebPageURL } from "../../../models/nominals";
import { IAPIPrice, IPrice } from "../../../models/prices";
import { getViewportBreakpoint } from "../../../utils/detectMobile";
import { usePageURL } from "../../../utils/urls";
import { AddToBasketButton } from "../AddToBasketButton";
import { ConfiguratorDeliveryDisclaimer } from "../ConfiguratorDeliveryDisclaimer";
import { ConfiguratorPriceLoading } from "../ConfiguratorPriceLoading";
import { PLCOptionPanel } from "../PLCOptionPanel";
import { PLCProductCategorySelector } from "../PLCProductCategorySelector";
import { PLCProductMatchModal } from "../PLCProductMatchModal";
import { RatingsSummary } from "../RatingsSummary";
import {
    IPLCOptionPanel,
    IPLCProductCategorySelector,
    IUpsellModalComponentFn,
} from "../models";

// Load styles
import "./PLCConfiguratorContent.scss";

const BEM_NAME = "plc-configurator";

const buildPrice = (
    price: IAPIPrice | null,
    upgradedVariant: IProduct | null,
    selectedAddonPrice: IPrice | null,
    financingLink: WebPageURL,
    priceError: string | null,
): React.ReactNode => {
    if (priceError || !price || !upgradedVariant) {
        return (
            <ConfiguratorPriceLoading
                configuratorType={ConfiguratorTypes.PLC}
                errorMessage={priceError}
            />
        );
    }
    return (
        <ConfiguratorPrice
            price={price}
            variant={upgradedVariant}
            selectedAddonPrice={selectedAddonPrice}
            configuratorType={ConfiguratorTypes.PLC}
            financingLink={financingLink}
            strikeThroughMSRP={true}
            discountDisplayMode={DiscountDisplayMode.DISABLED}
            actualPriceStyle={""}
        />
    );
};

const buildHeader = (
    preheader: string | undefined,
    title: string,
    subhead: string | undefined,
    starRatingURL: WebPageURL,
): React.ReactNode => {
    const text = (copy: string | undefined): React.ReactNode => {
        if (!copy) {
            return null;
        }
        return <RichText html={copy} />;
    };
    return (
        <div className={`${BEM_NAME}__header`}>
            {text(preheader)}
            <h3>{title}</h3>
            {text(subhead)}
            <RatingsSummary starRatingURL={starRatingURL} />
        </div>
    );
};

const buildDescription = (description: string | undefined): React.ReactNode => {
    if (!description) {
        return null;
    }
    return <RichText html={description} tagName="div" />;
};

const calculateConfiguratorBottomPos = (
    configuratorElem: HTMLElement | null,
): number => {
    if (!configuratorElem) {
        return 0;
    }
    const window = getWindow();
    return getViewportBreakpoint() < 1201
        ? (window?.innerHeight ?? 0) - 50
        : configuratorElem.getBoundingClientRect().bottom;
};

export const PLCConfigurator = React.memo(
    (props: {
        preheader?: string;
        title: string;
        subhead?: string;
        description?: string;
        footer: ISafeHTML;
        categorySelectors: readonly IPLCProductCategorySelector[];
        optionPanels: readonly IPLCOptionPanel[];
        upgradedVariant: IProduct | null;
        price: IAPIPrice | null;
        priceError: string | null;
        selectedAddonPrice: IPrice | null;
        starRatingURL: WebPageURL;
        basketLink: WebPageURL;
        configureGiftsLink: WebPageURL;
        showShippingLead?: boolean;
        shippingDescription: string | null;
        rootProduct: IProduct | null;
        phoneNumber?: string;
        getDeliveryCopy?: (
            rootProduct: IProduct,
            isPreorder: boolean,
        ) => string | null;
        getDeliveryIcon?: () => IImageURL | null;
        showUpsellModal?: (product: IProduct | null) => boolean;
        getUpsellModalComponentFn?: (
            product: IProduct,
        ) => IUpsellModalComponentFn | null;
    }): React.ReactNode => {
        const [configuratorBottomPos, setConfiguratorBottomPos] =
            React.useState<number>(0);
        const configuratorRef = React.useRef<HTMLElement>(null);

        const financeLinkURL = usePageURL("finance_link");
        const homeURL = usePageURL("home");
        const financingLink = financeLinkURL || homeURL || "";

        // Sticky rail: pin `.sticky` once the page scrolls past the site
        // header, and drop it on resize. The header height is read once on
        // mount (matching the legacy behavior), and the ref-guard keeps scroll
        // events from re-rendering except when the sticky state actually flips.
        const [isSticky, setIsSticky] = React.useState(false);
        const isStickyRef = React.useRef(false);

        React.useEffect(() => {
            const newPos = calculateConfiguratorBottomPos(
                configuratorRef.current,
            );
            setConfiguratorBottomPos(newPos);
        });

        React.useEffect(() => {
            const window = getWindow();
            const document = getDocument();
            if (!window || !document) {
                return;
            }
            const siteHeader =
                document.querySelector<HTMLElement>("header.header");
            const target = siteHeader ? siteHeader.offsetHeight : 0;
            const setSticky = (next: boolean): void => {
                if (isStickyRef.current !== next) {
                    isStickyRef.current = next;
                    setIsSticky(next);
                }
            };
            const onScroll = (): void => {
                setSticky(window.scrollY >= target);
            };
            const onResize = (): void => {
                setSticky(false);
            };
            window.addEventListener("scroll", onScroll);
            window.addEventListener("resize", onResize);
            // Sync initial state: with browser scroll restoration (refresh on a
            // deep link, or back/forward nav) the page can load already scrolled
            // past the header, so `.sticky` must apply on first paint rather than
            // waiting for the next scroll event.
            onScroll();
            return () => {
                window.removeEventListener("scroll", onScroll);
                window.removeEventListener("resize", onResize);
            };
        }, []);

        const variantIsAvailableToBuy = !!(
            props.upgradedVariant &&
            props.upgradedVariant.availability.is_available_to_buy
        );

        return (
            <article
                ref={configuratorRef}
                className={isSticky ? `${BEM_NAME} sticky` : BEM_NAME}
            >
                {buildHeader(
                    props.preheader,
                    props.title,
                    props.subhead,
                    props.starRatingURL,
                )}
                {buildDescription(props.description)}
                {props.categorySelectors.map((selector) => (
                    <PLCProductCategorySelector
                        key={selector.id}
                        categorySelectors={props.categorySelectors}
                        optionPanels={props.optionPanels}
                        selector={selector}
                    />
                ))}
                {props.optionPanels.map((panel) => (
                    <PLCOptionPanel
                        key={panel.id}
                        categorySelectors={props.categorySelectors}
                        optionPanels={props.optionPanels}
                        panel={panel}
                    />
                ))}
                <ConfiguratorDeliveryDisclaimer
                    configuratorType={ConfiguratorTypes.PLC}
                    variantIsAvailableToBuy={variantIsAvailableToBuy}
                    rootProduct={props.rootProduct}
                    upgradedVariant={props.upgradedVariant}
                    showShippingLead={props.showShippingLead}
                    phoneNumber={props.phoneNumber}
                    getDeliveryCopy={props.getDeliveryCopy}
                    getDeliveryIcon={props.getDeliveryIcon}
                />
                {buildPrice(
                    props.price,
                    props.upgradedVariant,
                    props.selectedAddonPrice,
                    financingLink,
                    props.priceError,
                )}
                <AddToBasketButton
                    basketLink={props.basketLink}
                    configureGiftsLink={props.configureGiftsLink}
                    configuratorType={ConfiguratorTypes.PLC}
                    buttonColor={"primary-congress-blue"}
                    showUpsellModal={props.showUpsellModal}
                    getUpsellModalComponentFn={props.getUpsellModalComponentFn}
                />
                {props.footer && (
                    <RichText
                        className={`${BEM_NAME}__footer`}
                        html={props.footer}
                    />
                )}
                <PLCProductMatchModal
                    categorySelectors={props.categorySelectors}
                    optionPanels={props.optionPanels}
                    modalPosition={configuratorBottomPos}
                />
            </article>
        );
    },
);
